import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { Microscope, Loader2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import AdminNavigation from '@/components/AdminNavigation';
import { Button } from '@/components/ui/button';

// Local components
import { GradioUrlCard } from './GradioUrlCard';
import { ImageUploadCard } from './ImageUploadCard';
import { ResultsCard } from './ResultsCard';
import { HistoryCard } from './HistoryCard';
import { API, DEFAULT_GRADIO_URL } from './helpers';

export default function PlanktonIdentifier() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [gradioUrl, setGradioUrl] = useState(DEFAULT_GRADIO_URL);
  const [loading, setLoading] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);

  // Handle file drop
  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (file) {
      setSelectedFile(file);
      setPreview(URL.createObjectURL(file));
      setResult(null);
      setError(null);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg', '.tiff', '.tif']
    },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024 // 10MB
  });

  // Test Gradio connection
  const testConnection = async () => {
    if (!gradioUrl.trim()) {
      toast.error('Vennligst skriv inn Gradio URL');
      return;
    }

    setTestingConnection(true);
    try {
      const formData = new FormData();
      formData.append('gradio_url', gradioUrl);

      const response = await axios.post(`${API}/plankton/test-connection`, formData, {
        withCredentials: true
      });

      if (response.data.gradio_health && response.data.has_predict_endpoint) {
        toast.success('Tilkobling OK! Gradio API er tilgjengelig.');
      } else if (response.data.gradio_health) {
        toast.warning('Gradio funnet, men predict-endepunkt mangler.');
      } else {
        toast.error(response.data.message || 'Kunne ikke koble til Gradio API');
      }
    } catch (err) {
      toast.error('Feil ved testing av tilkobling');
    } finally {
      setTestingConnection(false);
    }
  };

  // Identify plankton
  const identifyPlankton = async () => {
    if (!selectedFile) {
      toast.error('Vennligst velg et bilde');
      return;
    }
    if (!gradioUrl.trim()) {
      toast.error('Vennligst skriv inn Gradio URL');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('image', selectedFile);
      formData.append('gradio_url', gradioUrl);

      const response = await axios.post(`${API}/plankton/identify`, formData, {
        withCredentials: true,
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        timeout: 120000 // 2 minute timeout
      });

      setResult(response.data);
      toast.success('Plankton identifisert!');
    } catch (err) {
      console.error('Identification error:', err);
      const errorMessage = err.response?.data?.detail || 'Kunne ikke identifisere plankton';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Load identification history
  const loadHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await axios.get(`${API}/plankton/identifications`, {
        withCredentials: true
      });
      setHistory(response.data.identifications || []);
    } catch (err) {
      toast.error('Kunne ikke laste historikk');
    } finally {
      setLoadingHistory(false);
    }
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedFile(null);
    setPreview(null);
    setResult(null);
    setError(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-teal-50/30">
      <AdminNavigation />
      <div className="p-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center">
              <Microscope className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Plankton Identifikasjon</h1>
              <p className="text-slate-600">Bruk AI for å identifisere planktonarter fra bilder</p>
            </div>
          </div>
          <p className="text-sm text-slate-500 mt-2">
            Drevet av <a href="https://dashboard.cloud.imagine-ai.eu/catalog/modules/phyto-plankton-classification" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:underline inline-flex items-center gap-1">
              iMagine Phytoplankton Classifier <ExternalLink className="w-3 h-3" />
            </a> - 95 planktonklasser
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Upload and Token */}
          <div className="space-y-6">
            <GradioUrlCard
              gradioUrl={gradioUrl}
              setGradioUrl={setGradioUrl}
              testingConnection={testingConnection}
              onTestConnection={testConnection}
            />

            <ImageUploadCard
              preview={preview}
              selectedFile={selectedFile}
              getRootProps={getRootProps}
              getInputProps={getInputProps}
              isDragActive={isDragActive}
              onClear={clearSelection}
            />

            {/* Identify Button */}
            <Button
              onClick={identifyPlankton}
              disabled={!selectedFile || !gradioUrl.trim() || loading}
              className="w-full h-12 bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" />
                  Analyserer bilde...
                </>
              ) : (
                <>
                  <Microscope className="w-5 h-5 mr-2" />
                  Identifiser plankton
                </>
              )}
            </Button>
          </div>

          {/* Right Column - Results */}
          <div className="space-y-6">
            <ResultsCard
              loading={loading}
              error={error}
              result={result}
            />

            <HistoryCard
              showHistory={showHistory}
              setShowHistory={setShowHistory}
              history={history}
              loadingHistory={loadingHistory}
              onLoadHistory={loadHistory}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
